import { cn } from "@designs-by-maryam/ui/lib/utils";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "motion/react";
import { getStaggerDelay, useSafeMotion } from "@/lib/motion";
import type { ProcessStep } from "./about-content";

type ProcessTimelineProps = {
	stages: ProcessStep[] | readonly ProcessStep[];
	className?: string;
};

/**
 * ProcessTimeline Component
 *
 * Displays a four-stage design process with animated visual timeline.
 *
 * Features:
 * - Horizontal layout on desktop, vertical on mobile
 * - Animated step indicators with gradient circles
 * - Animated connecting lines with arrows
 * - Staggered entrance animations
 * - Hover effects with scale and glow
 * - Check mark completion indicators
 * - Semantic ordered list for accessibility
 */
export function ProcessTimeline({ stages, className }: ProcessTimelineProps) {
	const { shouldReduce, slideUp, scaleIn } = useSafeMotion();

	return (
		<section
			aria-labelledby="process-heading"
			className={cn("relative overflow-hidden py-16 md:py-24", className)}
		>
			{/* Background decoration */}
			<div
				aria-hidden="true"
				className="absolute inset-0 opacity-[0.015]"
				style={{
					backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px),
                           linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
					backgroundSize: "60px 60px",
				}}
			/>

			<div className="container relative mx-auto px-4 md:px-6">
				{/* Section Header */}
				<motion.div
					animate={slideUp.animate}
					className="mb-16 text-center"
					initial={slideUp.initial}
					transition={slideUp.transition}
				>
					<div className="mb-4 flex justify-center">
						<CheckCircle2 className="h-8 w-8 text-primary" />
					</div>
					<h2
						className="font-bold text-3xl text-foreground tracking-tight md:text-4xl"
						id="process-heading"
					>
						Design Process
					</h2>
					<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
						A proven methodology that ensures exceptional results
					</p>
				</motion.div>

				{/* Timeline */}
				<ol className="relative flex flex-col gap-12 lg:flex-row lg:gap-0">
					{stages.map((stage, index) => {
						const isLast = index === stages.length - 1;
						return (
							<motion.li
								animate={scaleIn.animate}
								className="relative flex-1"
								initial={scaleIn.initial}
								key={stage.step}
								transition={{
									duration: shouldReduce ? 0.01 : 0.5,
									delay: getStaggerDelay(index, 0.2, 0.2, shouldReduce),
								}}
							>
								<div className="group flex items-start gap-4 lg:flex-col lg:items-center lg:text-center">
									{/* Step Number Circle with gradient and glow */}
									<div className="relative">
										<motion.div
											className="relative z-10 flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/90 font-bold text-lg text-white shadow-xl ring-4 ring-primary/20 transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl dark:from-primary dark:to-primary/80 dark:ring-primary/10"
											whileHover={{ rotate: [0, -5, 5, 0] }}
										>
											{String(stage.step).padStart(2, "0")}
											{/* Inner glow */}
											<div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 to-transparent" />
										</motion.div>

										{/* Subtle glow on hover */}
										<div className="-z-10 absolute inset-0 rounded-full bg-primary/20 opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-100" />
									</div>

									{/* Content Card */}
									<div className="flex-1 rounded-lg border border-border/30 bg-card/30 p-6 pb-6 shadow-sm backdrop-blur-sm transition-all duration-300 group-hover:border-primary/30 group-hover:bg-card/50 group-hover:shadow-md lg:mt-8 lg:pb-6 dark:group-hover:border-primary/20">
										<h3 className="mb-3 font-bold text-foreground text-lg leading-tight">
											{stage.title}
										</h3>
										<p className="text-muted-foreground text-sm leading-relaxed">
											{stage.description}
										</p>
									</div>
								</div>

								{/* Connector Line - vertical on mobile, horizontal on desktop */}
								{!isLast && (
									<>
										{/* Mobile: vertical animated line */}
										<motion.div
											animate={{ scaleY: 1, opacity: 1 }}
											aria-hidden="true"
											className="absolute top-16 left-8 h-12 w-0.5 origin-top bg-gradient-to-b from-primary to-primary/80 lg:hidden"
											initial={{ scaleY: 0, opacity: 0 }}
											transition={{
												duration: shouldReduce ? 0.01 : 0.6,
												delay: getStaggerDelay(index, 0.4, 0.2, shouldReduce),
											}}
										/>

										{/* Desktop: horizontal animated line with arrow */}
										<motion.div
											animate={{ scaleX: 1, opacity: 1 }}
											aria-hidden="true"
											className="absolute top-8 left-16 hidden h-[2px] origin-left bg-gradient-to-r from-primary via-primary/80 to-primary/60 lg:block"
											initial={{ scaleX: 0, opacity: 0 }}
											style={{ width: "calc(100% - 4rem)" }}
											transition={{
												duration: shouldReduce ? 0.01 : 0.6,
												delay: getStaggerDelay(index, 0.6, 0.2, shouldReduce),
											}}
										/>

										{/* Animated arrow */}
										<motion.div
											animate={{ x: 0, opacity: 1 }}
											aria-hidden="true"
											className="absolute top-[1.85rem] right-0 hidden lg:block"
											initial={{ x: -10, opacity: 0 }}
											transition={{
												duration: shouldReduce ? 0.01 : 0.4,
												delay: getStaggerDelay(index, 0.8, 0.2, shouldReduce),
											}}
										>
											<ArrowRight className="h-5 w-5 text-primary" />
										</motion.div>

										{/* Static dot marker */}
										<div
											aria-hidden="true"
											className="absolute top-[1.75rem] hidden lg:block"
											style={{ left: "calc(50% - 1px)" }}
										>
											<div className="h-1.5 w-1.5 rounded-full bg-primary" />
										</div>
									</>
								)}
							</motion.li>
						);
					})}
				</ol>
			</div>
		</section>
	);
}
